import { useState, useEffect } from 'react';

import { Product } from '../types/product.type';

type ProductReference = { id: string; name: string };

const EMPTY_REFERENCE: ProductReference = { id: '', name: '' };

type UseProductEditFormResult = {
  isEditing: boolean;
  updatedProduct: Product;
  handleFieldChange: ({ field, value }: { field: keyof Product; value: unknown }) => void;
  handleCategoryChange: (category: ProductReference | null) => void;
  handleSubCategoryChange: (subCategory: ProductReference | null) => void;
  handleCancelEditing: () => void;
  handleSave: (updateFn: (editedFields: Partial<Product>) => Promise<void>) => Promise<void>;
};

export function useProductEditForm(product: Product): UseProductEditFormResult {
  const [isEditing, setIsEditing] = useState(false);
  const [updatedProduct, setUpdatedProduct] = useState<Product>(product);
  const [editedFields, setEditedFields] = useState<Partial<Product>>({});

  useEffect(() => {
    setUpdatedProduct(product);
    setEditedFields({});
    setIsEditing(false);
  }, [product]);

  const handleFieldChange = ({ field, value }: { field: keyof Product; value: unknown }) => {
    setIsEditing(true);
    setUpdatedProduct((previous) => ({ ...previous, [field]: value }));
    setEditedFields((previous) => ({ ...previous, [field]: value }));
  };

  const handleCategoryChange = (category: ProductReference | null) => {
    setIsEditing(true);
    setUpdatedProduct((previous) => ({
      ...previous,
      category: category ?? EMPTY_REFERENCE,
      subCategory: EMPTY_REFERENCE,
    }));
    setEditedFields((previous) => {
      const next: Partial<Product> = { ...previous, category: category ?? EMPTY_REFERENCE };
      delete next.subCategory;
      return next;
    });
  };

  const handleSubCategoryChange = (subCategory: ProductReference | null) => {
    setIsEditing(true);
    setUpdatedProduct((previous) => ({ ...previous, subCategory: subCategory ?? EMPTY_REFERENCE }));
    setEditedFields((previous) => ({ ...previous, subCategory: subCategory ?? EMPTY_REFERENCE }));
  };

  const handleCancelEditing = () => {
    setUpdatedProduct(product);
    setEditedFields({});
    setIsEditing(false);
  };

  const handleSave = async (updateFn: (editedFields: Partial<Product>) => Promise<void>) => {
    if (Object.keys(editedFields).length === 0) return;
    await updateFn(editedFields);
  };

  return {
    isEditing,
    updatedProduct,
    handleFieldChange,
    handleCategoryChange,
    handleSubCategoryChange,
    handleCancelEditing,
    handleSave,
  };
}
